import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { IStartSigningEnvConfig } from '@app/core/model/env-config.model';
import {
  IClientSignField,
  ISignFieldRectangle,
} from '@app/transaction-builder/model/sign-field.model';
import {
  MIN_POSSIBLE_SIGN_FIELD_HEIGHT_PX,
  MIN_POSSIBLE_SIGN_FIELD_WIDTH_PX,
} from '@app/transaction-builder/transaction-builder.constants';
import { EnvConfigService, IPoint, ISize } from 'ngx-mat-esign360';

@Component({
  selector: 'app-sign-field-canvas',
  templateUrl: './sign-field-canvas.component.html',
  styleUrls: ['./sign-field-canvas.component.scss'],
})
export class SignFieldCanvasComponent implements OnInit {
  @Input() minAllowedSignFieldSize: ISize;
  @Output() drawingStarted = new EventEmitter<void>();
  @Output() drawingFinished = new EventEmitter<ISignFieldRectangle>();
  signField: IClientSignField;
  isDrawing = false;
  drawingStartPoint: IPoint;
  topLeft: IPoint;
  bottomRight: IPoint;
  readonly SIGN_FIELD_POINT_WIDTH_PX = 5;
  readonly SIGN_FIELD_POINT_HEIGHT_PX = 5;

  get doesSignFieldHaveMinPossibleSize(): boolean {
    return (
      this.bottomRight.x - this.topLeft.x > MIN_POSSIBLE_SIGN_FIELD_WIDTH_PX &&
      this.bottomRight.y - this.topLeft.y > MIN_POSSIBLE_SIGN_FIELD_HEIGHT_PX
    );
  }

  get doesSignFieldHaveMinAllowedSize(): boolean {
    return (
      this.bottomRight.x - this.topLeft.x > this.minAllowedSignFieldSize.width &&
      this.bottomRight.y - this.topLeft.y > this.minAllowedSignFieldSize.height
    );
  }

  constructor(
    private elementRef: ElementRef,
    public envConfig: EnvConfigService<IStartSigningEnvConfig>
  ) {}

  ngOnInit(): void {}

  getMousePositionOnCanvas(event): IPoint {
    const canvasRect: DOMRect = this.elementRef.nativeElement.getBoundingClientRect();
    return { x: (event.pageX - canvasRect.left) * 0.75, y: (event.pageY - canvasRect.top) * 0.75 };
  }

  @HostListener('contextmenu', ['$event'])
  onContextMenu(event) {
    event.preventDefault();
    this.cancelDrawing();
  }

  @HostListener('mousedown', ['$event'])
  onStartDrawing(event: MouseEvent) {
    const canvasPoint = this.getMousePositionOnCanvas(event);
    this.startDrawing(canvasPoint);
  }

  @HostListener('window:mouseup', ['$event'])
  onFinishDrawing(event: MouseEvent) {
    if (this.isDrawing) {
      const canvasPoint = this.getMousePositionOnCanvas(event);
      this.finishDrawing(canvasPoint);
    }
  }

  @HostListener('mousemove', ['$event'])
  onCanvasMousemove(event: MouseEvent) {
    if (this.isDrawing) {
      this.draw(event);
    }
  }

  startDrawing(point: IPoint) {
    this.isDrawing = true;
    this.drawingStartPoint = point;
    this.topLeft = point;
    this.bottomRight = point;

    this.drawingStarted.emit();
  }

  draw(event) {
    const firstPoint = this.drawingStartPoint;
    const secondPoint = this.getMousePositionOnCanvas(event);

    this.topLeft = {
      x: Math.min(firstPoint.x, secondPoint.x),
      y: Math.min(firstPoint.y, secondPoint.y),
    };
    this.bottomRight = {
      x: Math.max(firstPoint.x, secondPoint.x),
      y: Math.max(firstPoint.y, secondPoint.y),
    };
  }

  cancelDrawing() {
    this.isDrawing = false;
  }

  finishDrawing(point: IPoint) {
    this.isDrawing = false;

    if (this.doesSignFieldHaveMinPossibleSize && this.doesSignFieldHaveMinAllowedSize) {
      this.drawingFinished.emit({
        topLeft: this.topLeft,
        bottomRight: this.bottomRight,
      });
    }
  }
}
